import { connect } from 'react-redux';
import {
    Text,
    Avatar,
    createTableColumn,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow,
    Spinner,
    TableCellLayout,
    TableColumnDefinition,
    Toolbar,
    ToolbarButton,
    Button,
    TeachingPopover,
    TeachingPopoverTrigger,
    TeachingPopoverSurface,
    TeachingPopoverHeader,
    TeachingPopoverBody
} from '@fluentui/react-components';
import { useEffect, useMemo, useState } from 'react';

// styles
import './Debts.List.Styles.css';

//icons 
import {
    Add16Regular,
    EditRegular,
    DeleteRegular,
    Comment20Regular,
    CheckmarkCircle20Regular,
    DismissCircle20Regular,
    Search20Regular,
    EyeRegular,
    CalendarLtr20Regular
} from '@fluentui/react-icons';

// Component functions
import * as ComponentFunctions from './Debts.List.Functions';

// models
import { Debt } from '../../../../models/Entities/CashFlow/Debt/Debt';

// other components
import moment from 'moment';
import DebtFormComponent from '../Debt.Form/Debt.Form.Component';
import DebtDetailsComponent from '../Debt.Details/Debt.Details.Component';
import { FormType } from '../Debt.Form/Debt.Form.Functions';
import ConfirmationModalComponent from '../../../../commons/component/ConfirmationModal/ConformationModal.Component';

const formatAmount = (amount: number | undefined, currency: string | undefined): string => {
    try {
        return (amount ?? 0).toLocaleString('es-AR', {
            style: 'currency',
            currency: (currency || 'ARS').toUpperCase()
        });
    } catch {
        return `${(currency || 'ARS').toUpperCase()} ${(amount ?? 0).toFixed(2)}`;
    }
};

const DebtListComponent = (props: ComponentFunctions.Props) => {

    // add or update form control
    const [isDebtFormOpen, setIsDebtFormOpen] = useState(false);
    const [selectedDebt, setSelectedDebt] = useState<Debt | null>(null);

    // details drawer
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [detailsDebtId, setDetailsDebtId] = useState<number | undefined>(undefined);

    // events
    const onDebtFormDismiss = () => {
        setSelectedDebt(null);
        setIsDebtFormOpen(false);
    };

    const onViewDebt = (debt: Debt) => {
        setDetailsDebtId(debt.id);
        setIsDetailsOpen(true);
    };

    const onDetailsDismiss = () => {
        setIsDetailsOpen(false);
        setDetailsDebtId(undefined);
    };

    const onEditDebt = (debt: Debt) => {
        setSelectedDebt(debt);
        setIsDebtFormOpen(true);
    };

    const onDeleteDebt = (debt: Debt) => {
        props.DeleteDebt(debt.id);
    };

    useEffect(() => {
        props.GetAllPersons();
        switch (props.type) {
            case ComponentFunctions.DebtListType.Debtor:
                props.GetAllDebtsByDebtor(props.EntityIdentifier, props.FixedAssociatedEntityIdentifier);
                break;
            case ComponentFunctions.DebtListType.Creditor:
                props.GetAllDebtsByCreditor(props.EntityIdentifier, props.FixedAssociatedEntityIdentifier);
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredDebts = useMemo(() => {
        const currentFilter = props.filter ?? ComponentFunctions.DebtListFilter.All;
        switch (currentFilter) {
            case ComponentFunctions.DebtListFilter.PendingOnly:
                return props.Debts?.filter(d => d.pendingAmount > 0 && !d.isDebtSettled) ?? [];
            case ComponentFunctions.DebtListFilter.SettledOnly:
                return props.Debts?.filter(d => d.pendingAmount <= 0 || d.isDebtSettled) ?? [];
            default:
                return props.Debts ?? [];
        }
    }, [props.Debts, props.filter]);

    const filterLabel = useMemo(() => {
        switch (props.filter) {
            case ComponentFunctions.DebtListFilter.PendingOnly:
                return 'Pendientes';
            case ComponentFunctions.DebtListFilter.SettledOnly:
                return 'Saldadas';
            default:
                return 'Todas';
        }
    }, [props.filter]);

    const Forms = (
        <>
            <DebtFormComponent
                FormType={props.type === ComponentFunctions.DebtListType.Creditor ? FormType.Creditor : FormType.Debtor}
                ReferalId={props.EntityIdentifier}
                FixedAssociatedEntityIdentifier={props.FixedAssociatedEntityIdentifier}
                isOpen={isDebtFormOpen}
                DebtId={selectedDebt?.id}
                onClose={onDebtFormDismiss}
            />
            <DebtDetailsComponent
                DebtId={detailsDebtId}
                isOpen={isDetailsOpen}
                onClose={onDetailsDismiss}
            />
        </>
    );

    const renderStatusPill = (item: Debt): JSX.Element => {
        const isSettled = item.pendingAmount <= 0 || item.isDebtSettled;
        if (isSettled) {
            return (
                <span className='debtsList__statusPill debtsList__statusPill--settled'>
                    <CheckmarkCircle20Regular style={{ width: 14, height: 14 }} />
                    Saldada
                </span>
            );
        }
        return (
            <span className='debtsList__statusPill debtsList__statusPill--pending'>
                <DismissCircle20Regular style={{ width: 14, height: 14 }} />
                Pendiente
            </span>
        );
    };

    const renderDebtVerifySection = (item: Debt): JSX.Element | null => {
        if (props.filter !== ComponentFunctions.DebtListFilter.SettledOnly) return null;

        if (props.isCheckingDebtStatus && props.checkedDebtStatusId === item.id) {
            return <Spinner size='extra-tiny' />;
        }

        if (!props.isCheckingDebtStatus && props.successCheckingDebtStatus && props.checkedDebtStatusId === item.id) {
            const verifyClass = props.debtStatusResult
                ? 'debtsList__verify debtsList__verify--ok'
                : 'debtsList__verify debtsList__verify--bad';
            return (
                <span className={verifyClass}>
                    {props.debtStatusResult ? <CheckmarkCircle20Regular /> : <DismissCircle20Regular />}
                    {props.debtStatusResult ? 'Saldada' : 'Con saldo'}
                </span>
            );
        }

        return (
            <Button
                appearance='subtle'
                size='small'
                aria-label='Verificar estado'
                icon={<Search20Regular />}
                title='Verificar estado en el sistema'
                onClick={(e) => { e.stopPropagation(); props.CheckDebtStatus(item.id); }}
            />
        );
    };

    const columns: TableColumnDefinition<Debt>[] = [
        createTableColumn<Debt>({
            columnId: 'debtor',
            compare: (a, b) => {
                const an = props.GetPersonByIdentifier(a.debtor)?.businessName || '';
                const bn = props.GetPersonByIdentifier(b.debtor)?.businessName || '';
                return an.localeCompare(bn);
            },
            renderHeaderCell: () => 'Deudor',
            renderCell: (item: Debt) => {
                const name = props.GetPersonByIdentifier(item.debtor)?.businessName;
                return (
                    <TableCellLayout
                        truncate
                        media={(
                            <Avatar
                                aria-label={item.debtor}
                                name={name}
                                color='colorful'
                                size={28}
                            />
                        )}
                    >
                        {name}
                    </TableCellLayout>
                );
            }
        }),
        createTableColumn<Debt>({
            columnId: 'date',
            compare: (a, b) => new Date(a.debtDate).getTime() - new Date(b.debtDate).getTime(),
            renderHeaderCell: () => 'Fecha',
            renderCell: (item: Debt) => (
                <TableCellLayout>
                    <span className='debtsList__datePill'>
                        <CalendarLtr20Regular style={{ width: 14, height: 14, color: '#94a3b8' }} />
                        {moment(item.debtDate).format('DD/MM/YYYY')}
                    </span>
                </TableCellLayout>
            )
        }),
        createTableColumn<Debt>({
            columnId: 'creditor',
            compare: (a, b) => {
                const an = props.GetPersonByIdentifier(a.creditor)?.businessName || '';
                const bn = props.GetPersonByIdentifier(b.creditor)?.businessName || '';
                return an.localeCompare(bn);
            },
            renderHeaderCell: () => 'Acreedor',
            renderCell: (item: Debt) => {
                const name = props.GetPersonByIdentifier(item.creditor)?.businessName;
                return (
                    <TableCellLayout
                        truncate
                        media={(
                            <Avatar
                                aria-label={item.creditor}
                                name={name}
                                color='colorful'
                                size={28}
                            />
                        )}
                    >
                        {name}
                    </TableCellLayout>
                );
            }
        }),
        createTableColumn<Debt>({
            columnId: 'status',
            renderHeaderCell: () => 'Estado',
            renderCell: (item: Debt) => (
                <TableCellLayout>
                    {renderStatusPill(item)}
                </TableCellLayout>
            )
        }),
        createTableColumn<Debt>({
            columnId: 'originaAmount',
            compare: (a, b) => a.originalAmount - b.originalAmount,
            renderHeaderCell: () => 'Monto',
            renderCell: (item: Debt) => (
                <TableCellLayout>
                    <span className='debtsList__amount'>
                        {formatAmount(item.originalAmount, item.currencyCode)}
                    </span>
                </TableCellLayout>
            )
        }),
        createTableColumn<Debt>({
            columnId: 'paidAmount',
            compare: (a, b) => a.paidAmount - b.paidAmount,
            renderHeaderCell: () => 'Pagado',
            renderCell: (item: Debt) => (
                <TableCellLayout>
                    <span className='debtsList__amount debtsList__amount--paid'>
                        {formatAmount(item.paidAmount, item.currencyCode)}
                    </span>
                </TableCellLayout>
            )
        }),
        createTableColumn<Debt>({
            columnId: 'pendingAmount',
            compare: (a, b) => a.pendingAmount - b.pendingAmount,
            renderHeaderCell: () => 'Pendiente',
            renderCell: (item: Debt) => {
                const isSettled = item.pendingAmount <= 0 || item.isDebtSettled;
                const cls = isSettled
                    ? 'debtsList__amount debtsList__amount--settled'
                    : 'debtsList__amount debtsList__amount--pending';
                return (
                    <TableCellLayout>
                        <span className={cls}>
                            {formatAmount(item.pendingAmount, item.currencyCode)}
                        </span>
                    </TableCellLayout>
                );
            }
        }),
        createTableColumn<Debt>({
            columnId: 'comments',
            compare: (a, b) => (a.comments || '').localeCompare(b.comments || ''),
            renderHeaderCell: () => 'Comentarios',
            renderCell: (item: Debt) => {
                if (!item.comments) {
                    return (
                        <TableCellLayout>
                            <Text size={100} style={{ color: '#94a3b8' }}>—</Text>
                        </TableCellLayout>
                    );
                }
                return (
                    <TableCellLayout>
                        <TeachingPopover>
                            <TeachingPopoverTrigger>
                                <Button
                                    appearance='transparent'
                                    size='small'
                                    icon={<Comment20Regular />}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Ver
                                </Button>
                            </TeachingPopoverTrigger>
                            <TeachingPopoverSurface>
                                <TeachingPopoverHeader>Comentarios</TeachingPopoverHeader>
                                <TeachingPopoverBody>
                                    <Text>{item.comments}</Text>
                                </TeachingPopoverBody>
                            </TeachingPopoverSurface>
                        </TeachingPopover>
                    </TableCellLayout>
                );
            }
        }),
        createTableColumn<Debt>({
            columnId: 'actions',
            renderHeaderCell: () => 'Acciones',
            renderCell: (item: Debt) => {
                const isSettled = item.pendingAmount <= 0 || item.isDebtSettled;
                const debtorName = props.GetPersonByIdentifier(item.debtor)?.businessName || item.debtor;
                return (
                    <div className='debtsList__actions'>
                        <Button
                            appearance='subtle'
                            size='small'
                            aria-label='Ver detalle'
                            title='Ver detalle'
                            icon={<EyeRegular />}
                            onClick={(event) => { event.stopPropagation(); onViewDebt(item); }}
                        />
                        <Button
                            appearance='subtle'
                            size='small'
                            aria-label='Editar'
                            title='Editar'
                            icon={<EditRegular />}
                            onClick={(event) => { event.stopPropagation(); onEditDebt(item); }}
                        />
                        <div
                            onClick={(event) => event.stopPropagation()}
                            onMouseDown={(event) => event.stopPropagation()}
                        >
                            {isSettled ? (
                                <Button
                                    appearance='transparent'
                                    size='small'
                                    aria-label='Eliminar'
                                    title='No se puede eliminar una deuda saldada'
                                    icon={<DeleteRegular />}
                                    disabled
                                />
                            ) : (
                                <ConfirmationModalComponent
                                    onDismiss={() => { }}
                                    onConfirm={() => onDeleteDebt(item)}
                                    title='Eliminar deuda'
                                    message={`¿Confirma que desea eliminar la deuda de ${debtorName} por ${formatAmount(item.originalAmount, item.currencyCode)}?`}
                                    buttonApparence='subtle'
                                    buttonShape='square'
                                    buttonTitle=''
                                    buttonIcon={<DeleteRegular />}
                                    isProcessing={props.isDeleting}
                                />
                            )}
                        </div>
                        {renderDebtVerifySection(item)}
                    </div>
                );
            }
        })
    ];

    const totalCount = filteredDebts.length;
    const showEmpty = props.isLoadingSuccessfully && totalCount === 0;

    return (
        <div className='debtsList'>
            <div className='debtsList__header'>
                <div className='debtsList__headerLeft'>
                    <span className='debtsList__title'>
                        {'Deudas '}
                        <span className='debtsList__titleBadge'>{totalCount}</span>
                    </span>
                    <span className='debtsList__subtitle'>
                        Filtro activo: {filterLabel} · {props.type === ComponentFunctions.DebtListType.Creditor ? 'Como acreedor' : 'Como deudor'}
                    </span>
                </div>
                <div className='debtsList__headerRight'>
                    <Toolbar aria-label='Acciones' className='debtsList__toolbar'>
                        <ToolbarButton
                            aria-label='Agregar deuda'
                            appearance='primary'
                            icon={<Add16Regular />}
                            onClick={() => setIsDebtFormOpen(true)}
                        >
                            Agregar
                        </ToolbarButton>
                    </Toolbar>
                </div>
            </div>

            <div className='debtsList__divider' />

            {showEmpty ? (
                <div className='debtsList__empty'>
                    <img
                        src={require('../../../../assets/img/nodata.png')}
                        alt='Sin datos'
                        className='debtsList__emptyIcon'
                    />
                    <span className='debtsList__emptyTitle'>No hay pendientes registrados</span>
                    <span className='debtsList__emptySubtitle'>
                        Agregá una nueva deuda con el botón "Agregar".
                    </span>
                </div>
            ) : (
                <div className='debtsList__gridWrapper'>
                    <DataGrid
                        items={filteredDebts}
                        columns={columns}
                        sortable
                        selectionMode='multiselect'
                        getRowId={(item) => item.id}
                        focusMode='composite'
                    >
                        <DataGridHeader>
                            <DataGridRow
                                selectionCell={{ checkboxIndicator: { 'aria-label': 'Seleccionar todas las filas' } }}
                            >
                                {({ renderHeaderCell }) => (
                                    <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                                )}
                            </DataGridRow>
                        </DataGridHeader>
                        <DataGridBody<Debt>>
                            {({ item, rowId }) => (
                                <DataGridRow<Debt>
                                    key={rowId}
                                    selectionCell={{ checkboxIndicator: { 'aria-label': 'Seleccionar fila' } }}
                                >
                                    {({ renderCell }) => (
                                        <DataGridCell>{renderCell(item)}</DataGridCell>
                                    )}
                                </DataGridRow>
                            )}
                        </DataGridBody>
                    </DataGrid>
                </div>
            )}

            {props.isLoading && (
                <div className='debtsList__loading'>
                    <Spinner size='extra-tiny' />
                    Cargando deudas…
                </div>
            )}

            {Forms}
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(DebtListComponent as any);
